import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { DialogStatus } from 'src/app/core/services/dialog.service';
import { DialogService } from './dialog.service';

import { RegularExpressions } from '../constants/regular-expressions';

import * as FileSaver from 'file-saver';
import { ConsoleService } from './console.service';
const EXCEL_EXTENSION = '.xlsx';
const EXCEL_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  constructor(
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    public dialogService: DialogService
  ) {}

  checkFileType(file, fileTypes: string[]): boolean {
    let fileType = file.name.split('.');
    fileType = fileType[fileType.length - 1];

    if (!fileTypes.find((item) => item === fileType.toLowerCase())) {
      let message = this.translate.instant('messages.validFileTypes', {
        types: fileTypes.join(),
      });
      this.dialogService.openDialog({
        title: 'common.error',
        message: message,
        status: DialogStatus.error,
      });
      return false;
    }

    return true;
  }

  checkFileSizeWithMb(file, maxFileSize: number) {
    let maxFileSizeByte = maxFileSize * 2 ** 20;

    if (file?.size > maxFileSizeByte) {
      let message = this.translate.instant('messages.fileMaxLength', {
        size: maxFileSize,
      });
      this.dialogService.openDialog({
        title: 'common.error',
        message: message,
        status: DialogStatus.error,
      });
      return false;
    }

    return true;
  }

  checkFilesCount(files, maxFileCount: number) {
    if (files?.length > maxFileCount) {
      let message = this.translate.instant('messages.maxFileCount', {
        count: maxFileCount,
      });
      this.dialogService.openDialog({
        title: 'common.error',
        message: message,
        status: DialogStatus.error,
      });
      return false;
    }

    return true;
  }

  checkFileSizeWithKB(file, maxFileSize: number) {
    let maxFileSizeByte = maxFileSize * 1000;

    if (file?.size > maxFileSizeByte) {
      let message = this.translate.instant('messages.fileMaxLengthInKB', {
        size: maxFileSize,
      });
      this.dialogService.openDialog({
        title: 'common.error',
        message: message,
        status: DialogStatus.error,
      });
      return false;
    }

    return true;
  }

  checkFileSizeWithGB(file, maxFileSize: number) {
    let maxFileSizeByte = maxFileSize * 2 ** 30;

    if (file?.size > maxFileSizeByte) {
      let message = this.translate.instant('messages.fileMaxLengthInGB', {
        size: maxFileSize,
      });
      this.dialogService.openDialog({
        title: 'common.error',
        message: message,
        status: DialogStatus.error,
      });
      return false;
    }

    return true;
  }

  getFileType(file): string {
    if (!file) return;
    let fileType = file?.name.split('.');
    return fileType[fileType.length - 1];
  }

  isValidColumnNames(columnNames: string[]): boolean {
    let result: boolean = true;
    let pattern = RegularExpressions.justEnglishAndNumericCharacters;

    let errorMessage = this.translate.instant('messages.excelColListError');

    columnNames.forEach((colText) => {
      if (!result) return;
      if (!pattern.test(colText)) {
        result = false;
        return result;
      }
    });

    if (!result) {
      this.dialogService.openDialog({
        title: 'common.error',
        message: errorMessage,
        status: DialogStatus.error,
      });
    }

    return result;
  }

  readTxtFile(file): Observable<string> {
    const fileReader = new FileReader();
    const observable = new Observable<string>((subscriber) => {
      try {
        let text;
        fileReader.onload = (e) => {
          text = fileReader.result.toString().trim();
          subscriber.next(text);
          subscriber.complete();
        };
        fileReader.readAsText(file);
      } catch (error) {
        ConsoleService.error('error', error);
        subscriber.error(error);
      }
    });
    return observable;
  }

  readExcelFile(file): Observable<ExcelFileDataDto> {
    const observable = new Observable<ExcelFileDataDto>((subscriber) => {
      let result: ExcelFileDataDto = {};
      result.columnNames = [];
      try {
        const fileReader = new FileReader();
        fileReader.readAsArrayBuffer(file!);
        fileReader.onload = (event) => {
          const arrayBuffer: any = fileReader.result;
          let data = new Uint8Array(arrayBuffer);
          let arr = new Array();
          for (var i = 0; i != data.length; ++i)
            arr[i] = String.fromCharCode(data[i]);
          let bstr = arr.join('');
          import('xlsx').then((xlsx) => {
            let workbook = xlsx.read(bstr, { type: 'binary' });
            let first_sheet_name = workbook.SheetNames[0];
            let worksheet = workbook.Sheets[first_sheet_name];
            result.jsonData = xlsx.utils.sheet_to_json(worksheet, {
              raw: true,
            });
            // get columnNames
            if (result.jsonData.length > 0) {
              let obj1 = result.jsonData[0];
              let keys = Object.keys(obj1);
              keys.forEach((item) => {
                result.columnNames.push(item);
              });
            }
            //emit
            subscriber.next(result);
            subscriber.complete();
          });
        };
      } catch (error) {
        ConsoleService.error('import excel file error', error);
        subscriber.error(error);
      }
    });
    return observable;
  }

  createTextFile(data): any {
    const blob = new Blob([data], { type: 'application/octet-stream' });
    let fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      window.URL.createObjectURL(blob)
    );
    return fileUrl;
  }

  public exportAsExcelFile(json: any[], excelFileName: string): void {
    import('xlsx').then((xlsx) => {
      const myworksheet = xlsx.utils.json_to_sheet(json);
      const myworkbook = {
        Sheets: { data: myworksheet },
        SheetNames: ['data'],
      };
      const excelBuffer: any = xlsx.write(myworkbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      this.saveAsExcelFile(excelBuffer, excelFileName);
    });
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    FileSaver.saveAs(data, fileName + '_exported' + EXCEL_EXTENSION);
  }
}

export class ExcelFileDataDto {
  jsonData?: Array<Object>;
  columnNames?: string[] = [];
}
